import { action, makeAutoObservable } from 'mobx';
import { logScrollTrigger } from '@utils/scrollDebug';
import { DefaultSession } from 'next-auth';
import { ICategory } from '@features/categories/typescript';

const LAST_VISITED_CATEGORY_KEY = 'lastVisitedCategoryId';
const LAST_VISITED_PRODUCT_KEY = 'lastVisitedProductId';

class LayoutUiState {
  isSearchOpened = false;

  isPromotionalMessagesOpened = false;

  barPositionStatic = false;

  isMenuDesktopHighlighted = false;

  isMenuMobileOpened = false;

  userPanelOpened = false;

  isLoginGeneralOpened = false;

  isVideoModalOpened = false;

  navigationAndFooterVisible = true;

  isCartCreated = false;

  isSideBarOpened = false;

  navigationProductItemSelected: ICategory | null = null;

  isMenuPined = false;

  navigationCurrentProduct: ICategory | null = null;

  navigationMarketingProduct: ICategory | null = null;

  userData: DefaultSession | boolean = false;

  lastVisitedCategoryId: number | null = null;

  lastVisitedProductId: number | null = null; // TODO: remove this field after fixing product listing pagination

  /** True only on browser back from KP — gates ProductList scroll restore (not forward click). */
  listingScrollRestorePending = false;

  langPopupHeight: number | null = null;

  isCategoriesFiltersAsideActive = false;

  isProductCardOpened = false;

  navigationContainerIsStatic = true;

  navigationContainerHeight = 100;

  navigationContainerIsVisible = true;

  redirectSearchForm: string | null = null;

  pagesScrollY: Record<string, { documentHeight: number; scrollY: number }> = {};

  constructor() {
    makeAutoObservable(this);
    this.initLastVisitedCategoryId();
    this.initLastVisitedProductId();
  }

  private initLastVisitedCategoryId = () => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(LAST_VISITED_CATEGORY_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!Number.isNaN(parsed)) {
          this.lastVisitedCategoryId = parsed;
        }
      }
    }
  };

  private initLastVisitedProductId = () => {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(LAST_VISITED_PRODUCT_KEY);
      if (stored) {
        const parsed = parseInt(stored, 10);
        if (!Number.isNaN(parsed)) {
          this.lastVisitedProductId = parsed;
        }
      }
    }
  };

  public setNavigationAndFooterVisible = action((value: boolean) => {
    this.navigationAndFooterVisible = value;
  });

  public openSearch = action(() => {
    this.isSearchOpened = true;
    this.setNavigationProductItemSelected(null);
    this.setUserData(false);
    this.isMenuMobileOpened = false;
    this.isMenuPined = false;
  });

  public closeSearch = action(() => {
    this.isSearchOpened = false;
  });

  public setBarsPositionStatic = action((value: boolean) => {
    this.barPositionStatic = value;
  });

  public setIsPromotionalMessagesOpened = action((value: boolean) => {
    this.isPromotionalMessagesOpened = value;
    this.setBarsPositionStatic(!value);
  });

  public setMenuDesktopHighlighted = action((value: boolean) => {
    this.isMenuDesktopHighlighted = value;
  });

  public setMenuMobileOpened = action((value: boolean) => {
    this.isMenuMobileOpened = value;
    this.setMenuPined(false);
    this.setLoginGeneralOpened(false);
  });

  public setUserPanelOpened = action((value: boolean) => {
    this.userPanelOpened = value;
  });

  public setCartCreated = action((value: boolean) => {
    this.isCartCreated = value;
  });

  public setVideoModalOpened = action((value: boolean) => {
    this.isVideoModalOpened = value;
  });

  public setNavigationProductItemSelected = action((product: ICategory | null) => {
    if (this.navigationProductItemSelected?.id !== product?.id) {
      this.navigationProductItemSelected = product;
      this.setMenuPined(true);
    } else {
      this.navigationProductItemSelected = null;
      this.setMenuPined(false);
    }
  });

  public setMenuPined = action((value: boolean) => {
    this.isMenuPined = value;
  });

  public setLoginGeneralOpened = action((value: boolean) => {
    this.isLoginGeneralOpened = value;
  });

  public setNavigationCurrentProduct = action((value: ICategory | null) => {
    this.navigationCurrentProduct = value;
  });

  public setNavigationMarketingProduct = action((value: ICategory | null) => {
    this.navigationMarketingProduct = value;
  });

  public setUserData = action((value: DefaultSession | boolean) => {
    this.userData = value;
    this.setUserPanelOpened(!!value);
  });

  public setIsSideBarOpened = action((value: boolean) => {
    this.isSideBarOpened = value;
  });

  public setIsCategoriesFiltersAsideActive = action((value: boolean) => {
    this.isCategoriesFiltersAsideActive = value;
  });

  public setLastVisitedCategoryId = action((categoryId: number | null) => {
    this.lastVisitedCategoryId = categoryId;
    if (typeof window !== 'undefined') {
      if (categoryId !== null) {
        sessionStorage.setItem(LAST_VISITED_CATEGORY_KEY, categoryId.toString());
      } else {
        sessionStorage.removeItem(LAST_VISITED_CATEGORY_KEY);
      }
    }
  });

  public setListingScrollRestorePending = action((value: boolean) => {
    this.listingScrollRestorePending = value;
  });

  public setLastVisitedProductId = action((productId: number | null) => {
    logScrollTrigger('LayoutUiState:setLastVisitedProductId', {
      productId,
      previousProductId: this.lastVisitedProductId,
      listingScrollRestorePending: this.listingScrollRestorePending,
    });
    this.lastVisitedProductId = productId;
    if (typeof window !== 'undefined') {
      if (productId !== null) {
        sessionStorage.setItem(LAST_VISITED_PRODUCT_KEY, productId.toString());
      } else {
        sessionStorage.removeItem(LAST_VISITED_PRODUCT_KEY);
      }
    }
  });

  public setLangPopupHeight = action((height: number) => {
    this.langPopupHeight = height;
  });

  public setIsProductCardOpened = action((value: boolean) => {
    this.isProductCardOpened = value;
  });

  public getLayoutBarsHeight = (): number => {
    let barsHeight = 0;
    if (this.isPromotionalMessagesOpened) barsHeight += 33;
    if (this.langPopupHeight) barsHeight += this.langPopupHeight;
    if (this.navigationContainerIsVisible && !this.navigationContainerIsStatic)
      barsHeight += this.navigationContainerHeight;
    return barsHeight;
  };

  public getLayoutFixedBarsHeight = (): number =>
    !this.barPositionStatic || this.navigationContainerIsVisible ? this.getLayoutBarsHeight() : 0;

  public getLayoutBarsTotalHeight = (): number =>
    this.isPromotionalMessagesOpened ? 33 + 102 + (this.langPopupHeight ?? 0) : 102 + (this.langPopupHeight ?? 0);

  public setNavigationContainerIsStatic = action((value: boolean) => {
    this.navigationContainerIsStatic = value;
  });

  public setNavigationContainerIsVisible = action((value: boolean) => {
    this.navigationContainerIsVisible = value;
  });

  public setNavigationContainerHeight = action((value: number) => {
    this.navigationContainerHeight = value;
  });

  public getRedirectSearchForm = (): string | null => this.redirectSearchForm;

  public setRedirectSearchForm = action((url: string | null) => {
    this.redirectSearchForm = url;
  });

  public setScrollYOnPage = action((pageSlug: string, documentHeight: number, scrollY: number) => {
    logScrollTrigger('LayoutUiState:setScrollYOnPage', { pageSlug, documentHeight, scrollY });
    this.pagesScrollY = {};
    this.pagesScrollY[pageSlug] = { documentHeight, scrollY };
  });

  public getScrollYOnPage = (pageSlug: string): { documentHeight: number; scrollY: number } | null =>
    this.pagesScrollY[pageSlug] ?? null;
}

export default new LayoutUiState();
